import { concat, isEmpty, pick } from 'lodash';
import Constants from 'js/Constants';
import * as actionTypes from 'js/constants/actionTypes';
import {
  ACLQueryModeTypes,
  AvailableFilterTypesByView,
  RESOURCE_SUGGESTIONS_COUNT,
  SearchFilterTypes,
  SearchFilterViews,
} from 'js/constants/visibilityFilters';
import { processSearchFilters } from 'js/lib/SearchFilterUtils';
import { CALL_PRIVATE_API } from 'js/middleware/api';
import { pipelineSchema } from 'js/schemas';
import type { Dispatch } from 'redux';

interface LoadMCPFlowsParams {
  page?: number;
  sort?: string;
  projectId?: string;
}

export const loadMCPFlows =
  ({ page = undefined, sort = undefined, projectId = undefined }: LoadMCPFlowsParams) =>
  (dispatch: Dispatch, getState: () => any) => {
    const {
      app: {
        accessControl,
        visibilityFilters: { filters, availableFilters },
        listing: { sortCriteria: listingSortCriteria },
      },
    } = getState();

    const aclQueryMode = accessControl?.mode ?? ACLQueryModeTypes.MODE_OWNER.key;

    const sortCriteria = sort || listingSortCriteria;
    const availableFilterTypes = AvailableFilterTypesByView[SearchFilterViews.MCP_FLOWS];
    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];
    const { processedFilters } = processSearchFilters(
      projectId
        ? concat(filters[SearchFilterViews.MCP_FLOWS], {
            token: SearchFilterTypes.PROJECT_ID,
            value: projectId,
          })
        : filters[SearchFilterViews.MCP_FLOWS],
      pick(availableFilters, availableFilterTypes),
    );

    let requestMethod: string;
    let endpoint: string;
    let payload: Record<string, unknown> = { agentic: true };

    if (isEmpty(processedFilters)) {
      requestMethod = 'GET';
      endpoint = '/flows';
      if (page) {
        payload.page = page;
        payload.per_page = Constants.LISTING_ITEMS_PER_PAGE;
        payload.sort_by = sortKey;
        payload.sort_order = sortOrder;
      }
    } else {
      requestMethod = 'POST';
      const searchParams = new URLSearchParams({
        page: String(page || 1),
        per_page: String(Constants.LISTING_ITEMS_PER_PAGE),
        sort_by: sortKey,
        sort_order: sortOrder,
        agentic: 'true',
      });
      endpoint = `/flows/search?${searchParams}`;
      payload = { filters: processedFilters };
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_MCP_FLOWS_REQUEST,
          actionTypes.GET_MCP_FLOWS_SUCCESS,
          actionTypes.GET_MCP_FLOWS_FAILURE,
        ],
        endpoint,
        payload,
        schema: pipelineSchema,
        requestMethod,
        aclQueryMode,
      },
    });
  };

export function loadMCPFlowsSearchSuggestions(
  filters: Array<{ token: string; value: string }>,
  availableFilters: Record<string, unknown>,
  projectId: string | undefined = undefined,
) {
  return (dispatch: Dispatch, getState: () => any) => {
    const { app } = getState();
    const aclQueryMode = app.accessControl.mode;

    const sortCriteria = Constants.SortCriteria.LAST_CREATED_DESC;
    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];

    const { processedFilters } = processSearchFilters(
      projectId
        ? concat(filters, {
            token: SearchFilterTypes.PROJECT_ID,
            value: projectId,
          })
        : filters,
      availableFilters,
    );

    if (isEmpty(processedFilters)) return;

    const params = new URLSearchParams({
      page: '1',
      per_page: String(RESOURCE_SUGGESTIONS_COUNT),
      sort_by: sortKey,
      sort_order: sortOrder,
      agentic: 'true',
    });

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_MCP_FLOWS_REQUEST,
          actionTypes.GET_MCP_FLOWS_SUCCESS,
          actionTypes.GET_MCP_FLOWS_FAILURE,
        ],
        endpoint: `/flows/search?${params}`,
        schema: pipelineSchema,
        requestMethod: 'POST',
        aclQueryMode,
        payload: { filters: processedFilters },
      },
    });
  };
}
